import React, {useEffect, useState} from 'react';
import '../ChestPurchaseModal/ChestPurchaseModal.css';
import '../RatingCrystalsRewardModal/RatingCrystalsRewardModal.css';
import './MenuPromoModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {sendPageEvent} from '../../../analytics/gameAnalytics';

/**
 * Общий корпус промо-модалок главного меню — приглашений в первый обычный
 * (TutorialFinishPromoModal) и первый рейтинговый (RankedMatchPromoModal)
 * матч. Тот же корпус и подсветка, что у модалок кристаллов за рейтинг
 * (RatingCrystalsRewardModal / RatingCrystalsInfoModal): заголовок, картинка,
 * абзацы текста (children) и две кнопки — действие и «Скрыть».
 *
 * Когда и кому показывать, решает меню (см. utils/menuPromo); reason уходит
 * уровнем события показа (page:<pageId>:mount:<reason>) — по нему видно,
 * первый это показ или напоминание. Кнопка действия закрывает модалку
 * сразу, без затухания: меню под ней все равно сменяется другим экраном;
 * «Скрыть» — с затуханием. Оба пути ведут в onClose — там меню ставит
 * отметки показа.
 *
 * @param {string} pageId - id для событий аналитики
 * @param {string} reason - причина показа (уровень события)
 * @param {string} title
 * @param {string} imagePath
 * @param {string} imageAlt
 * @param {{buttonId: string, label: string, className?: string, onClick: Function}} [action] -
 *   без действия у модалки одна кнопка — закрытие (так у анонсов: им некуда
 *   вести, кнопка лишь подтверждает прочтение)
 * @param {Function} onClose
 * @param {string} [modalClassName] - модификатор корпуса (например, своя
 *   подсветка вместо золотой у rating-crystals-modal)
 * @param {string} [titleClassName] - цвет заголовка, по умолчанию text-attention
 * @param {string} [hideLabel] - подпись кнопки закрытия, по умолчанию «Скрыть»
 * @param {string} [hideButtonId] - id кнопки закрытия для аналитики
 * @param {string} [hideButtonClassName] - стиль кнопки закрытия; у
 *   единственной кнопки — тот же, что был бы у действия
 */
const MenuPromoModal = ({
  pageId,
  reason,
  title,
  imagePath,
  imageAlt,
  action,
  onClose,
  modalClassName = '',
  titleClassName = 'text-attention',
  hideLabel = 'Скрыть',
  hideButtonId = 'hide',
  hideButtonClassName = 'main-menu-button_secondary',
  children,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Звук появления — общий для промо-модалок меню; звук разведки рудника
    // остался только у модалки начисления кристаллов за рейтинг
    MENU_SOUND_MANAGER.playModalRoundEnd();
    sendPageEvent(pageId, 'mount', reason);
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
    // Причина показа фиксируется в момент появления — менять событие задним
    // числом незачем
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHide = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const handleAction = () => {
    onClose?.();
    action.onClick?.();
  };

  return (
    <div
      className={`chest-purchase-overlay rating-crystals-overlay ${visible ? 'chest-purchase-overlay_visible' : ''}`}
      role={'dialog'}
      aria-modal={'true'}
    >
      <div
        className={`chest-purchase-modal rating-crystals-modal ${modalClassName} ${visible ? 'chest-purchase-modal_visible' : ''}`}
      >
        <div className={'chest-purchase-content'}>
          <h2 className={`rating-crystals-modal_title ${titleClassName}`}>{title}</h2>

          <div className={'chest-purchase-image-wrapper'}>
            <img className={'chest-purchase-image'} src={imagePath} alt={imageAlt}/>
          </div>

          {children}

          <div className={'main-menu-buttons chest-purchase-buttons'}>
            {action && (
              <PageButton
                className={action.className}
                onClick={handleAction}
                pageId={pageId}
                buttonId={action.buttonId}
              >
                {action.label}
              </PageButton>
            )}
            <PageButton
              className={hideButtonClassName}
              onClick={handleHide}
              pageId={pageId}
              buttonId={hideButtonId}
            >
              {hideLabel}
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuPromoModal;
